import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, ShieldCheck, FileText, Lock, XCircle } from "lucide-react";
import { BRAND } from "../data/content";
import { QUOTE_TERMS } from "../data/quoteTerms";
import { trackEvent } from "../lib/analytics";

// Public client-facing quote acceptance page. Lives outside the admin auth shell.
// Routed at /q/:token. No admin login required.
const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function QuoteAccept() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [sig, setSig] = useState({ signature_name: "", signature_role: "", terms_acknowledged: false });
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    axios.get(`${API}/quote-accept/${token}`)
      .then((r) => setQuote(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Quote not found"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!sig.signature_name.trim()) return setError("Please type your full name to sign");
    if (!sig.terms_acknowledged) return setError("You must tick the box to acknowledge the Terms & Conditions");
    setBusy(true); setError(null);
    try {
      await axios.post(`${API}/quote-accept/${token}`, sig);
      // High-value GA4 conversion — fire `purchase` so revenue rolls up in GA4
      // standard reports (Acquisition → Conversions). `transaction_id` ensures
      // GA4 dedupes if the customer double-clicks the accept button.
      trackEvent("purchase", {
        transaction_id: quote?.reference || token,
        value: Number(quote?.total) || 0,
        currency: "GBP",
        items: [{
          item_id: quote?.reference || token,
          item_name: quote?.service || "Compliance service",
          item_category: quote?.service || "compliance",
          price: Number(quote?.total) || 0,
          quantity: 1,
        }],
      });
      trackEvent("quote_accepted", {
        reference: quote?.reference || null,
        value: Number(quote?.total) || 0,
        currency: "GBP",
        service: quote?.service || null,
      });
      setAccepted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Acceptance failed. Please try again.");
    } finally { setBusy(false); }
  };

  const submitReject = async (e) => {
    e.preventDefault();
    if (!sig.signature_name.trim()) return setError("Please type your full name");
    if (!rejectReason.trim()) return setError("Please give a brief reason so we can revise the quote.");
    setBusy(true); setError(null);
    try {
      await axios.post(`${API}/quote-reject/${token}`, {
        signature_name: sig.signature_name,
        signature_role: sig.signature_role,
        reason: rejectReason,
      });
      trackEvent("quote_rejected", {
        reference: quote?.reference || null,
        value: Number(quote?.total) || 0,
        currency: "GBP",
        service: quote?.service || null,
      });
      setRejected(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally { setBusy(false); }
  };

  if (error && !quote) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white border border-slate-200 p-10 max-w-md text-center shadow-sm">
          <h1 className="font-display text-2xl font-bold text-slate-900 mb-3">Quote unavailable</h1>
          <p className="text-slate-600">{error}</p>
          <p className="text-sm text-slate-500 mt-6">Please contact <a href={`mailto:${BRAND.email}`} className="text-[#1FA59A] underline">{BRAND.email}</a> or call {BRAND.phone}.</p>
        </div>
      </div>
    );
  }
  if (!quote) return <div className="min-h-screen flex justify-center items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  if (accepted || quote.status === "accepted") {
    return <AcceptedConfirmation quote={quote} signature={accepted ? sig : quote.acceptance} />;
  }
  if (rejected || quote.status === "rejected") {
    return <RejectedConfirmation quote={quote} signature={rejected ? { ...sig, reason: rejectReason } : quote.rejection} />;
  }

  const isLocked = ["expired"].includes(quote.status);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="page-quote-accept">
      {/* Header */}
      <div className="bg-[#0A0F1C] text-white p-8">
        <div className="max-w-3xl mx-auto flex items-center gap-5">
          <div className="bg-white p-2 rounded-sm">
            <img src={BRAND.logo} alt="OEC" className="h-12 w-auto" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#7DD3C7] mb-1">Quotation</p>
            <h1 className="font-display text-2xl lg:text-3xl font-bold">{quote.reference}</h1>
            <p className="text-sm text-white/70 mt-1">Prepared by {BRAND.legalName}</p>
          </div>
          <span className="text-[10px] uppercase tracking-wider font-semibold bg-emerald-50 text-emerald-700 px-3 py-1 rounded-sm flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Secure link
          </span>
        </div>
      </div>

      {/* Quote body */}
      <div className="max-w-3xl mx-auto bg-white shadow mt-6">
        <div className="p-8 border-b border-slate-100 grid grid-cols-2 gap-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">Prepared for</p>
            <p className="font-display text-lg font-bold">{quote.client_name}</p>
            {quote.client_company && <p className="text-sm">{quote.client_company}</p>}
            <p className="text-sm text-slate-600">{quote.client_email}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">Validity</p>
            <p className="text-sm">Issued <strong>{new Date(quote.created_at).toLocaleDateString("en-GB")}</strong></p>
            <p className="text-sm">Valid until <strong>{quote.valid_until}</strong></p>
          </div>
        </div>

        {quote.scope && (
          <div className="p-8 border-b border-slate-100">
            <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">Introduction</p>
            <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{quote.scope}</p>
          </div>
        )}

        {(quote.services?.length > 0 ? quote.services : null) ? (
          <div className="p-8 border-b border-slate-100 space-y-7">
            {quote.services.map((sec, sidx) => {
              const sub = (sec.line_items || []).reduce((s, li) => s + (parseFloat(li.qty) || 0) * (parseFloat(li.unit_price) || 0), 0);
              return (
                <div key={sidx}>
                  <h3 className="font-display text-lg font-bold border-b-2 border-slate-900 pb-2 mb-3">{sec.service}</h3>
                  {sec.scope && <p className="text-sm text-slate-700 mb-3 whitespace-pre-wrap leading-relaxed">{sec.scope}</p>}
                  <table className="w-full text-sm">
                    <tbody>
                      {(sec.line_items || []).map((li, i) => {
                        const qty = parseFloat(li.qty) || 0; const up = parseFloat(li.unit_price) || 0;
                        return (
                          <tr key={i} className="border-b border-slate-100">
                            <td className="py-1.5">{li.description}</td>
                            <td className="py-1.5 text-right text-slate-500">{qty} × £{up.toFixed(2)}</td>
                            <td className="py-1.5 text-right font-semibold w-28">£{(qty * up).toFixed(2)}</td>
                          </tr>
                        );
                      })}
                      <tr><td colSpan={2} className="py-1.5 text-right text-xs font-semibold text-slate-600">Section subtotal</td><td className="py-1.5 text-right font-bold">£{sub.toFixed(2)}</td></tr>
                    </tbody>
                  </table>
                </div>
              );
            })}
            <Totals quote={quote} />
          </div>
        ) : (
          <div className="p-8 border-b border-slate-100">
            <table className="w-full text-sm">
              <tbody>
                {(quote.line_items || []).map((li, i) => {
                  const qty = parseFloat(li.qty) || 0; const up = parseFloat(li.unit_price) || 0;
                  return (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-2">{li.description}</td>
                      <td className="py-2 text-right text-slate-500">{qty} × £{up.toFixed(2)}</td>
                      <td className="py-2 text-right font-semibold">£{(qty * up).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Totals quote={quote} />
          </div>
        )}

        {/* Acceptance form */}
        {isLocked ? (
          <div className="p-8 bg-red-50 border-l-4 border-red-600 text-red-800">
            <p className="font-semibold">This quote is {quote.status} and can no longer be accepted online.</p>
            <p className="text-sm mt-1">Please contact <a href={`mailto:${BRAND.email}`} className="underline">{BRAND.email}</a>.</p>
          </div>
        ) : rejectMode ? (
          <form onSubmit={submitReject} className="p-8 bg-amber-50 border-t-4 border-amber-500" data-testid="rejection-form">
            <h2 className="font-display text-xl font-bold mb-2">Decline &amp; request a revision</h2>
            <p className="text-sm text-slate-700 mb-5">Tell us what you'd like changed and we'll come back with a revised quote — usually within 24 hours.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Your full name *</label>
                <input required value={sig.signature_name} onChange={(e) => setSig({ ...sig, signature_name: e.target.value })} className="w-full border border-slate-300 px-4 py-3 bg-white focus:outline-none focus:border-[#1FA59A]" data-testid="reject-name" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Role / Position</label>
                <input value={sig.signature_role} onChange={(e) => setSig({ ...sig, signature_role: e.target.value })} placeholder="e.g. Property Manager" className="w-full border border-slate-300 px-4 py-3 bg-white focus:outline-none focus:border-[#1FA59A]" />
              </div>
            </div>

            <div className="mb-5">
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">What would you like revised? *</label>
              <textarea required rows={5} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="w-full border border-slate-300 px-4 py-3 bg-white focus:outline-none focus:border-[#1FA59A]" placeholder="e.g. Please remove the air monitoring line, or split the work across two visits, or adjust the rates for the second floor…" data-testid="reject-reason" />
            </div>

            {error && <p className="text-sm text-red-700 mb-4">{error}</p>}

            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white font-bold px-6 py-3 flex items-center gap-2 disabled:opacity-50" data-testid="reject-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-5 h-5" />}
                {busy ? "Submitting…" : "Decline & request revision"}
              </button>
              <button type="button" onClick={() => { setRejectMode(false); setError(null); }} className="border border-slate-300 hover:border-slate-900 px-6 py-3 font-semibold text-sm" data-testid="reject-cancel">
                Back
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={submit} className="p-8 bg-slate-50" data-testid="acceptance-form">
            <h2 className="font-display text-xl font-bold mb-2">Digital acceptance</h2>
            <p className="text-sm text-slate-600 mb-5">By signing below you confirm acceptance of this quotation and our Terms &amp; Conditions of service. We capture your IP address and timestamp as evidence for our ISO-compliant audit trail.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Your full name *</label>
                <input required value={sig.signature_name} onChange={(e) => setSig({ ...sig, signature_name: e.target.value })} className="w-full border border-slate-300 px-4 py-3 bg-white focus:outline-none focus:border-[#1FA59A]" data-testid="sig-name" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Role / Position</label>
                <input value={sig.signature_role} onChange={(e) => setSig({ ...sig, signature_role: e.target.value })} placeholder="e.g. Property Manager" className="w-full border border-slate-300 px-4 py-3 bg-white focus:outline-none focus:border-[#1FA59A]" />
              </div>
            </div>

            <label className="flex items-start gap-3 mb-5 cursor-pointer">
              <input type="checkbox" checked={sig.terms_acknowledged} onChange={(e) => setSig({ ...sig, terms_acknowledged: e.target.checked })} className="mt-1 w-4 h-4 accent-[#3FBAB0]" data-testid="sig-tc" />
              <span className="text-sm text-slate-700">I have read and agree to the <details className="inline-block"><summary className="inline cursor-pointer text-[#1FA59A] underline">OEC Terms &amp; Conditions of supply of services</summary><div className="text-xs text-slate-600 mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap bg-white border border-slate-200 p-4">{QUOTE_TERMS}</div></details>.</span>
            </label>

            {error && <p className="text-sm text-red-700 mb-4">{error}</p>}

            <div className="flex flex-wrap items-center gap-4">
              <button type="submit" disabled={busy} className="gs-gradient text-white font-bold px-8 py-4 flex items-center gap-3 hover:opacity-90 disabled:opacity-50" data-testid="sig-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                {busy ? "Recording acceptance…" : `Accept quote ${quote.reference}`}
              </button>
              <button type="button" onClick={() => { setRejectMode(true); setError(null); }} className="text-sm text-slate-600 hover:text-amber-700 underline font-medium" data-testid="open-reject">
                Or decline &amp; request a revision
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Submission is captured against IP, browser and timestamp.</p>
          </form>
        )}

        <div className="bg-[#0A0F1C] text-white/70 text-[10px] tracking-wider p-6 text-center uppercase">
          {BRAND.legalName} · {BRAND.address.line1}, {BRAND.address.city} {BRAND.address.postcode} · {BRAND.phone} · {BRAND.email}
        </div>
      </div>
    </div>
  );
}

function Totals({ quote }) {
  return (
    <div className="ml-auto w-72 mt-4 pt-3 border-t-2 border-slate-900 text-sm space-y-1">
      <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-semibold">£{(quote.subtotal || 0).toFixed(2)}</span></div>
      <div className="flex justify-between"><span className="text-slate-600">VAT @ 20%</span><span className="font-semibold">£{(quote.vat || 0).toFixed(2)}</span></div>
      <div className="flex justify-between text-base pt-1 mt-1 border-t border-slate-300"><span className="font-bold">Total inc. VAT</span><span className="font-bold gs-gradient-text text-lg">£{(quote.total || 0).toFixed(2)}</span></div>
    </div>
  );
}

function AcceptedConfirmation({ quote, signature }) {
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6" data-testid="acceptance-confirmation">
      <div className="bg-white border border-slate-200 p-10 max-w-md text-center shadow-sm">
        <div className="w-16 h-16 mx-auto bg-emerald-50 rounded-full flex items-center justify-center mb-5">
          <CheckCircle2 className="w-9 h-9 text-emerald-600" />
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Quote accepted</h1>
        <p className="text-slate-600 mb-1">Reference <strong>{quote.reference}</strong></p>
        {signature?.signature_name && <p className="text-sm text-slate-500 mb-5">Digitally signed by <strong>{signature.signature_name}</strong>{signature.signature_role ? `, ${signature.signature_role}` : ""}</p>}
        <div className="bg-slate-50 p-4 text-left text-xs text-slate-600 mt-6 space-y-1">
          <p className="flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> A confirmation copy is being sent to {quote.client_email}.</p>
          <p className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> The OEC team has been notified and will be in touch shortly to schedule the works.</p>
        </div>
        <p className="text-xs text-slate-400 mt-6">Questions? Email <a href={`mailto:${BRAND.email}`} className="underline">{BRAND.email}</a> or call {BRAND.phone}.</p>
      </div>
    </div>
  );
}

function RejectedConfirmation({ quote, signature }) {
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6" data-testid="rejection-confirmation">
      <div className="bg-white border border-slate-200 p-10 max-w-md text-center shadow-sm">
        <div className="w-16 h-16 mx-auto bg-amber-50 rounded-full flex items-center justify-center mb-5">
          <XCircle className="w-9 h-9 text-amber-600" />
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Revision requested</h1>
        <p className="text-slate-600 mb-1">Reference <strong>{quote.reference}</strong></p>
        {signature?.signature_name && <p className="text-sm text-slate-500 mb-3">Submitted by <strong>{signature.signature_name}</strong>{signature.signature_role ? `, ${signature.signature_role}` : ""}</p>}
        {signature?.reason && (
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 text-left text-sm text-slate-700 mt-5 whitespace-pre-wrap">{signature.reason}</div>
        )}
        <div className="bg-slate-50 p-4 text-left text-xs text-slate-600 mt-5 space-y-1">
          <p className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> The OEC team has been notified and will come back to you with a revised quote — usually within 24 hours.</p>
        </div>
        <p className="text-xs text-slate-400 mt-6">Questions? Email <a href={`mailto:${BRAND.email}`} className="underline">{BRAND.email}</a> or call {BRAND.phone}.</p>
      </div>
    </div>
  );
}
